"use client";

import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import type { ToolUIPart } from "ai";
import {
  CheckCircleIcon,
  CircleIcon,
  ClockIcon,
  WrenchIcon,
  XCircleIcon,
} from "lucide-react";
// CaretDown, not lucide's ChevronDownIcon — the timeline/reasoning surfaces
// this card can render inside (CompletedToolsGroup) already use Phosphor's
// CaretDown; two chevron glyphs in the same vertical inch read as sloppy
// (CHAT_UI_DESIGN_REVIEW.md §4 — "pick one chevron glyph family").
import { CaretDown } from "@phosphor-icons/react";
import type { ComponentProps, ReactNode } from "react";
import { isValidElement } from "react";
import { CodeBlock } from "./code-block";

export type ToolProps = ComponentProps<typeof Collapsible>;

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible
    className={cn("not-prose mb-4 w-full rounded-md border", className)}
    {...props}
  />
);

export type ToolHeaderProps = {
  title?: string;
  /** One-line human-readable hint of what the call is doing (truncated). */
  summary?: string;
  type: ToolUIPart["type"];
  state: ToolUIPart["state"];
  className?: string;
};

/**
 * Bounded pretty-JSON for tool payloads. Full dumps of large tool results
 * into the DOM cause layout thrash and giant scroll regions — cap them and
 * tell the user what was omitted.
 */
const MAX_JSON_CHARS = 3_000;

export function formatBoundedJson(value: unknown): string {
  let serialized: string;
  try {
    serialized = typeof value === "string" ? value : JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    serialized = String(value);
  }
  if (serialized.length <= MAX_JSON_CHARS) {
    return serialized;
  }
  return `${serialized.slice(0, MAX_JSON_CHARS)}\n… truncated (${serialized.length.toLocaleString()} chars total)`;
}

/**
 * Picks the most informative field from a tool input for the collapsed
 * header — `command`, `query`, `url`… — so users can tell what a call did
 * without expanding raw JSON.
 */
const SUMMARY_FIELDS = ["command", "query", "url", "prompt", "path", "filename", "name", "title", "code"] as const;

export function summarizeToolInput(input: unknown, maxChars = 96): string | undefined {
  if (typeof input === "string") {
    return input.length > maxChars ? `${input.slice(0, maxChars - 1)}…` : input;
  }
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return undefined;
  }
  const record = input as Record<string, unknown>;
  for (const field of SUMMARY_FIELDS) {
    const value = record[field];
    if (typeof value === "string" && value.trim()) {
      const compact = value.replace(/\s+/g, " ").trim();
      return compact.length > maxChars ? `${compact.slice(0, maxChars - 1)}…` : compact;
    }
  }
  return undefined;
}

export const getStatusBadge = (status: ToolUIPart["state"]) => {
  const labels: Record<ToolUIPart["state"], string> = {
    "input-streaming": "Pending",
    "input-available": "Running",
    "approval-requested": "Awaiting Approval",
    "approval-responded": "Responded",
    "output-available": "Completed",
    "output-error": "Error",
    "output-denied": "Denied",
  };

  const icons: Record<ToolUIPart["state"], ReactNode> = {
    "input-streaming": <CircleIcon className="size-4" />,
    // Ping dot instead of a pulsing clock — reads as live activity at a
    // glance without icon motion.
    "input-available": (
      <span className="relative flex size-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/50" />
        <span className="relative inline-flex size-2.5 rounded-full bg-primary" />
      </span>
    ),
    "approval-requested": <ClockIcon className="size-4 text-yellow-600" />,
    "approval-responded": <CheckCircleIcon className="size-4 text-blue-600" />,
    "output-available": <CheckCircleIcon className="size-4 text-green-600" />,
    "output-error": <XCircleIcon className="size-4 text-red-600" />,
    "output-denied": <XCircleIcon className="size-4 text-orange-600" />,
  };

  return (
    <Badge className="gap-1.5 rounded-full text-xs" variant="secondary">
      {icons[status]}
      {labels[status]}
    </Badge>
  );
};

export const ToolHeader = ({
  className,
  title,
  summary,
  type,
  state,
  ...props
}: ToolHeaderProps) => (
  <CollapsibleTrigger
    className={cn(
      "flex w-full items-center justify-between gap-4 p-3",
      className
    )}
    {...props}
  >
    <div className="flex items-center gap-2">
      <WrenchIcon className="size-4 shrink-0 text-muted-foreground" />
      <span className="shrink-0 font-medium text-sm">
        {title ?? type.split("-").slice(1).join("-")}
      </span>
      {summary && (
        <span className="min-w-0 truncate font-mono text-xs text-muted-foreground/70">
          {summary}
        </span>
      )}
      {getStatusBadge(state)}
    </div>
    <CaretDown weight="bold" aria-hidden="true" className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
  </CollapsibleTrigger>
);

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
      className
    )}
    {...props}
  />
);

export type ToolInputProps = ComponentProps<"div"> & {
  input: ToolUIPart["input"];
};

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => (
  <div className={cn("space-y-2 overflow-hidden p-4", className)} {...props}>
    <h4 className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-[1.5px]">
      Parameters
    </h4>
    <div className="max-h-64 overflow-auto rounded-md bg-muted/50">
      <CodeBlock code={formatBoundedJson(input)} language="json" />
    </div>
  </div>
);

export type ToolOutputProps = ComponentProps<"div"> & {
  output: ToolUIPart["output"];
  errorText: ToolUIPart["errorText"];
};

export const ToolOutput = ({
  className,
  output,
  errorText,
  ...props
}: ToolOutputProps) => {
  if (!(output || errorText)) {
    return null;
  }

  let Output = <div>{output as ReactNode}</div>;

  if (typeof output === "object" && !isValidElement(output)) {
    Output = (
      <CodeBlock code={formatBoundedJson(output)} language="json" />
    );
  } else if (typeof output === "string") {
    Output = <CodeBlock code={formatBoundedJson(output)} language="json" />;
  }

  return (
    <div className={cn("space-y-2 p-4", className)} {...props}>
      <h4 className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-[1.5px]">
        {errorText ? "Error" : "Result"}
      </h4>
      <div
        className={cn(
          "max-h-72 overflow-auto rounded-md text-xs [&_table]:w-full",
          errorText
            ? "bg-destructive/10 text-destructive"
            : "bg-muted/50 text-foreground"
        )}
      >
        {errorText && <div>{errorText}</div>}
        {Output}
      </div>
    </div>
  );
};
